import { useState } from 'react';
import { useBrokerQuery } from './queries/brokerQueries';
import { BrokerSearchField } from './components/BrokerSearchField';
import { ClearBrokerButton } from './components/ClearBrokerButton';
import { BrokerCard } from './components/BrokerCard/BrokerCard';
import './styles/_inputAlignedActions.scss';

interface BrokerSelectorProps {
    /** The selected broker. Changing it from outside (e.g. a loan loading in) resets the selector to it. */
    brokerId?: number;
    /** Called with the broker's id on every change (undefined when it's removed). */
    onChange: (brokerId: number | undefined) => void;
    disabled?: boolean;
}

/**
 * Picks a loan's broker (optional): the broker search, and the chosen broker's details below it. A new version of
 * BrokerSelect. It keeps the selection itself and hands it back through onChange.
 */
export const BrokerSelector = ({ brokerId, onChange, disabled = false }: BrokerSelectorProps) => {
    const [selectedBrokerId, set_selectedBrokerId] = useState(brokerId);
    const [lastBrokerId, set_lastBrokerId] = useState(brokerId);
    const { data: broker } = useBrokerQuery(selectedBrokerId);

    // Re-sync when the id passed in changes, unless it's just the one this handed back through onChange
    if (lastBrokerId !== brokerId) {
        set_lastBrokerId(brokerId);
        if (brokerId !== selectedBrokerId) {
            set_selectedBrokerId(brokerId);
        }
    }

    const selectBroker = (id: number | undefined) => {
        set_selectedBrokerId(id);
        onChange(id);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                <div style={{ flex: '1 1 0', minWidth: 0 }}>
                    <BrokerSearchField
                        value={selectedBrokerId}
                        onChange={selectBroker}
                        disabled={disabled}
                    />
                </div>
                <div className='brokerSelectorInputAlignedActions'>
                    <ClearBrokerButton
                        brokerId={selectedBrokerId}
                        onClear={() => selectBroker(undefined)}
                        disabled={disabled}
                    />
                </div>
            </div>
            {broker && <BrokerCard broker={broker} />}
        </div>
    );
};
